import React, { useEffect } from 'react'
import {
  Box,
  VStack,
  Text,
  Flex,
  Button,
  Icon,
  Image,
  HStack,
  useDisclosure,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import {
  FaFolder,
  FaPlus,
  FaBell,
  FaChevronLeft,
  FaChevronRight,
  FaSignOutAlt,
  FaUser,
  FaSignInAlt
} from 'react-icons/fa'
import CreateProject from '../project/CreateProject'
import { Project } from '../../_helpers/types'
import { useNavigate } from 'react-router-dom'
import { useAuthState } from 'react-firebase-hooks/auth'
import { BASE_URL } from '../../config'
import { auth } from '../../firebase'
import axios from 'axios'
import FeedbackButton from './FeedbackButton'
import { signOut } from 'firebase/auth'

interface SidebarProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  showSidebar: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onOpen, onClose, showSidebar }) => {
  const navigate = useNavigate()
  const [user, loading, error] = useAuthState(auth)
  const {
    isOpen: isCreateProjectOpen,
    onOpen: onCreateProjectOpen,
    onClose: onCreateProjectClose
  } = useDisclosure()

  const [projectsData, setProjectsData] = React.useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = React.useState(false)
  const [projectsError, setProjectsError] = React.useState<Error | null>(null)

  useEffect(() => {
    if (!error && user?.uid) {
      setProjectsLoading(true)
      axios
        .get(`${BASE_URL}/projects?userID=${user.uid}`)
        .then((response: { data: React.SetStateAction<Project[]> }) => {
          setProjectsData(response.data)
          setProjectsLoading(false)
        })
        .catch((error) => {
          setProjectsError(error)
          setProjectsLoading(false)
        })
    }
  }, [user?.uid])

  useEffect(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean)
    if (pathParts[0] === 'projects') {
      let projectId: string
      if (pathParts.length === 3) {
        [, projectId] = pathParts
      } else if (pathParts.length === 2) {
        [, projectId] = pathParts
      } else {
        return
      }

      // Check if the project is already in projectsData
      const projectExists = projectsData.some((project) => project.id === projectId)

      if (!projectExists) {
        setProjectsLoading(true)
        axios
          .get(`${BASE_URL}/projects/${projectId}`)
          .then((response) => {
            const newProject: Project = response.data

            setProjectsData((prevData) => {
              // Check again if the project already exists to prevent race conditions
              if (prevData.some((project) => project.id === newProject.id)) {
                return prevData
              }
              return [...prevData, newProject]
            })
            setProjectsLoading(false)
          })
          .catch((error) => {
            console.error('Error fetching project:', error)
            setProjectsLoading(false)
          })
      }
    }
  }, [])

  const isValid = !loading && !projectsLoading && !projectsError && projectsData

  useEffect(() => {
    onOpen()
  }, [onOpen])

  const handleSignOut = async () => {
    try {
      await signOut(auth)
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleSignIn = () => {
    navigate(`/login?returnUrl=${window.location.pathname}`)
  }

  if (!showSidebar) {
    return null
  }

  return (
    <>
      <Button
        position='fixed'
        top='5%'
        left={isOpen ? '300px' : '0'}
        transform='translateY(-50%)'
        zIndex='999'
        onClick={isOpen ? onClose : onOpen}
        colorScheme='orange'
        size='xs'
        borderRadius='0 4px 4px 0'
        transition='left 0.3s'
        height='30px'
      >
        <Icon as={isOpen ? FaChevronLeft : FaChevronRight} width='auto' />
      </Button>
      <Box
        position='fixed'
        left='0'
        top='0'
        bottom='0'
        width={isOpen ? '300px' : '0'}
        overflowY='auto'
        bg='orange.100'
        color='black'
        transition='width 0.3s'
        zIndex='998'
        display='flex'
        flexDirection='column'
      >
        <Box p={4} bg='orange.100'>
          <Flex justify='space-between' align='center'>
            <Image src='/logo.png' alt='logo' w='150px' />
            <HStack alignItems='center'>
              {/** Make this button actually open the notifications tab */}
              <Icon as={FaBell} w={5} h={5} mr={2} color='orange.600' />

              <Menu>
                <MenuButton>
                  {user && user.photoURL ? (
                    <Image
                      src={user.photoURL}
                      width='32px'
                      height='32px'
                      borderRadius='full'
                    />
                  ) : (
                    <Icon as={FaUser} w={5} h={5} mr={2} color='orange.600' />
                  )}
                </MenuButton>
                {user ? (
                  <MenuList>
                    <HStack my={1}>
                      <Icon as={FaUser} w={4} h={4} ml={3} mr='auto' color='orange.600' />
                      <Text fontSize='md' align='center' mr='auto'>
                        {user.email}
                      </Text>
                    </HStack>
                    <MenuItem icon={<FaSignOutAlt color='red' />} onClick={handleSignOut}>
                      Sign Out
                    </MenuItem>
                  </MenuList>
                ) : (
                  <MenuList>
                    <MenuItem icon={<FaSignInAlt color='green' />} onClick={handleSignIn}>
                      Sign In
                    </MenuItem>
                  </MenuList>
                )}
              </Menu>
            </HStack>
          </Flex>
        </Box>

        {projectsError && (
          <Box p={4} color='red.500'>
            <Text>Error loading projects: {projectsError.message}</Text>
          </Box>
        )}

        <VStack align='stretch' spacing={4} p={4}>
          <Button
            variant='ghost'
            justifyContent='flex-start'
            leftIcon={<Icon as={FaFolder} />}
            color='black'
            onClick={() => navigate('/projects')}
          >
            Library
          </Button>

          <Text fontWeight='bold'>Spaces</Text>

          {isValid ? (
            projectsData
              ?.sort((a: Project, b: Project) => {
                // Assuming your Project interface has a 'date' property
                const dateA = new Date(a.createdAt)
                const dateB = new Date(b.createdAt)
                return dateA.getTime() - dateB.getTime()
              })
              .reverse()
              .map((project: Project) => (
                <Button
                  key={project.id}
                  bg='orange.100'
                  color='black'
                  justifyContent='flex-start'
                  leftIcon={<Icon as={FaFolder} />}
                  _hover={{ bg: 'orange.200' }}
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <Text whiteSpace='nowrap' textOverflow='ellipsis' overflowX='hidden'>
                    {project.name}
                  </Text>
                </Button>
              ))
          ) : (
            <Flex justify='center'>
              <Spinner />
            </Flex>
          )}

          {user && (
            <Button
              variant='ghost'
              justifyContent='flex-start'
              leftIcon={<Icon as={FaPlus} />}
              onClick={onCreateProjectOpen}
              color='black'
            >
              New Project
            </Button>
          )}
        </VStack>

        <Box width={'fit-content'} px={4} mt='auto'>
          <FeedbackButton />
        </Box>
      </Box>
      <CreateProject isOpen={isCreateProjectOpen} onClose={onCreateProjectClose} />
    </>
  )
}

export default Sidebar
